import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import ChartPlaceholder from './ChartPlaceholder';

interface KaratDataPoint {
    readonly karatLabel: string;
    readonly currentTotalValue: number;
    readonly newTotalValue: number;
    readonly additionalCashNeeded: number;
}

interface GoldPriceChangeSimulationChartProps {
    readonly data: readonly KaratDataPoint[];
    readonly loading?: boolean;
    readonly currency?: 'EUR' | 'LEI';
}

function GoldPriceChangeSimulationChart({
    data,
    loading = false,
    currency = 'EUR',
}: GoldPriceChangeSimulationChartProps): JSX.Element {
    const { t } = useTranslation();

    if (loading || data.length === 0) {
        return <ChartPlaceholder loading={loading} hasData={data.length > 0} />;
    }

    const currencyLabel = currency === 'LEI' ? 'RON' : 'EUR';

    const categories = data.map(d => d.karatLabel);

    const options: Highcharts.Options = {
        chart: {
            type: 'column',
            height: 400,
        },
        title: {
            text: undefined,
        },
        xAxis: {
            categories,
            title: {
                text: t('statistics.simulations.karat', 'Karat'),
            },
        },
        yAxis: {
            title: {
                text: t('statistics.simulations.value', 'Value') + ` (${currencyLabel})`,
            },
            min: 0,
        },
        tooltip: {
            shared: true,
            valueDecimals: 0,
            valueSuffix: ` ${currencyLabel}`,
        },
        plotOptions: {
            column: {
                grouping: true,
                borderWidth: 0,
            },
        },
        series: [
            {
                type: 'column',
                name: t('statistics.simulations.currentValue', 'Current Value'),
                data: data.map(d => d.currentTotalValue),
                color: '#1890ff',
            },
            {
                type: 'column',
                name: t('statistics.simulations.newValue', 'New Value'),
                data: data.map(d => d.newTotalValue),
                color: '#52c41a',
            },
            {
                type: 'column',
                name: t('statistics.simulations.additionalCash', 'Additional Cash Needed'),
                data: data.map(d => d.additionalCashNeeded),
                color: '#fa541c',
            },
        ],
        credits: {
            enabled: false,
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default GoldPriceChangeSimulationChart;
